import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Link2,
  Unlink,
  CheckCircle,
  AlertCircle,
  Loader2,
  Copy,
  Info,
  Search,
  Users,
  RefreshCw
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import { cn } from '@/lib/utils';
import { obConnectionState } from './obConnectionState';
import {
  LwButton,
  LwModal,
  LwStatusPill
} from '@/components/lw';

const log = createComponentLogger('OptimalBlueCredentials', 'integration');

interface OBUserSafe {
  obUserId: string;
  username: string;
  firstName: string;
  lastName: string;
  email: string | null;
  accessLevel: string;
  businessChannelId: number;
}

interface OptimalBlueCredentialsProps {
  userId: string;
  userEmail: string;
  firstName?: string;
  lastName?: string;
  isAdminView?: boolean;
  presentation?: 'default' | 'profile';
}

export const OptimalBlueCredentials: React.FC<OptimalBlueCredentialsProps> = ({
  userId,
  isAdminView = false,
  presentation = 'default'
}) => {
  const isProfilePresentation = presentation === 'profile';
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searching, setSearching] = useState(false);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false);

  // Search state
  const [searchResults, setSearchResults] = useState<OBUserSafe[]>([]);
  const [totalAvailable, setTotalAvailable] = useState<number>(0);
  const [selectedObUserId, setSelectedObUserId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  // Linked state
  const [obUserId, setObUserId] = useState<string | null>(null);
  const [obUsername, setObUsername] = useState<string | null>(null);
  const [obLinkedAt, setObLinkedAt] = useState<Date | null>(null);
  const [obBusinessChannelId, setObBusinessChannelId] = useState<string | null>(null);

  useEffect(() => {
    loadObStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing legacy lint debt (not part of the OB identity fix)
  }, [userId]);

  const loadObStatus = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('users')
        .select('optimal_blue_user_id, optimal_blue_username, optimal_blue_linked_at, optimal_blue_business_channel_id')
        .eq('id', userId)
        .single();

      if (error) {
        log.error('Failed to load OB status', { userId }, error);
        return;
      }

      if (data) {
        setObUserId(data.optimal_blue_user_id || null);
        setObUsername(data.optimal_blue_username || null);
        setObLinkedAt(data.optimal_blue_linked_at ? new Date(data.optimal_blue_linked_at) : null);
        setObBusinessChannelId(data.optimal_blue_business_channel_id || null);
      }
    } catch (error) {
      log.error('Error loading OB status', { userId }, error as Error);
    } finally {
      setLoading(false);
    }
  };

  const searchObUsers = async (term?: string) => {
    try {
      setSearching(true);
      setSearchResults([]);

      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Session expired. Please refresh the page.');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ob-user-link`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            action: 'search',
            userId,
            searchTerm: term || undefined
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to search OB users');
      }

      const data = await response.json();
      setSearchResults(data.users || []);
      setTotalAvailable(data.available || 0);
      setHasSearched(true);

      log.info('OB search results', {
        total: data.total,
        available: data.available,
        linked: data.linked,
        returned: data.users?.length
      });
    } catch (error) {
      log.error('Error searching OB users', { userId }, error as Error);
      toast.error(error instanceof Error ? error.message : 'Failed to search OB users');
    } finally {
      setSearching(false);
    }
  };

  const handleLink = async () => {
    if (!selectedObUserId) {
return;
}

    try {
      setSyncing(true);

      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Session expired. Please refresh the page.');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ob-user-link`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            action: 'link',
            userId,
            obUserId: selectedObUserId
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to link OB user');
      }

      const data = await response.json();
      setObUserId(data.optimalBlueUserId);
      setObUsername(data.optimalBlueUsername);
      setObLinkedAt(data.optimalBlueLinkedAt ? new Date(data.optimalBlueLinkedAt) : new Date());
      setObBusinessChannelId(data.optimalBlueBusinessChannelId || null);
      setConnectDialogOpen(false);

      toast.success('Successfully linked to Optimal Blue user');
    } catch (error) {
      log.error('Error linking OB user', { userId, selectedObUserId }, error as Error);
      toast.error(error instanceof Error ? error.message : 'Failed to link OB user');
    } finally {
      setSyncing(false);
    }
  };

  const handleUnlink = async () => {
    try {
      setSyncing(true);

      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Session expired. Please refresh the page.');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ob-user-link`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ action: 'unlink', userId })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to unlink OB user');
      }

      setObUserId(null);
      setObUsername(null);
      setObLinkedAt(null);
      setObBusinessChannelId(null);
      setUnlinkDialogOpen(false);

      toast.success('Successfully unlinked Optimal Blue user');
    } catch (error) {
      log.error('Error unlinking OB user', { userId }, error as Error);
      toast.error(error instanceof Error ? error.message : 'Failed to unlink OB user');
    } finally {
      setSyncing(false);
    }
  };

  const handleRefreshIdentity = async () => {
    try {
      setSyncing(true);
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Session expired. Please refresh the page.');
        return;
      }
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ob-user-link`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ action: 'refresh_identity', userId })
        }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to refresh OB identity');
      }
      const data = await response.json();
      setObUserId(data.optimalBlueUserId);
      setObUsername(data.optimalBlueUsername);
      setObBusinessChannelId(data.optimalBlueBusinessChannelId || null);
      if (data.optimalBlueBusinessChannelId) {
        toast.success('Business channel repaired from Optimal Blue');
      } else {
        toast.warning('Refreshed, but Optimal Blue still has no business channel for this user. Assign one in OB, then repair again.');
      }
    } catch (error) {
      log.error('Error refreshing OB identity', { userId }, error as Error);
      toast.error(error instanceof Error ? error.message : 'Failed to refresh OB identity');
    } finally {
      setSyncing(false);
    }
  };

  const copyObId = () => {
    if (obUserId) {
      navigator.clipboard.writeText(obUserId);
      toast.success('OB User ID copied to clipboard');
    }
  };

  const obState = obConnectionState(obUserId, obBusinessChannelId);
  const connected = obState === 'connected';

  const getStatusBadge = () => {
    if (obState === 'connected') {
      return (
        <Badge variant="outline" className={cn('bg-green-50 text-green-700 border-green-200', isProfilePresentation && 'lw-credential-card__status')}>
          <CheckCircle className="w-3 h-3 mr-1" />
          Connected
        </Badge>
      );
    }
    if (obState === 'partial') {
      return (
        <Badge variant="outline" className={cn('bg-amber-50 text-amber-700 border-amber-200', isProfilePresentation && 'lw-credential-card__status')}>
          <AlertCircle className="w-3 h-3 mr-1" />
          Partial &middot; needs channel
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className={cn('bg-gray-50 text-gray-700 border-gray-200', isProfilePresentation && 'lw-credential-card__status')}>
        <AlertCircle className="w-3 h-3 mr-1" />
        Not Connected
      </Badge>
    );
  };

  const openConnectDialog = () => {
    setSelectedObUserId('');
    setSearchResults([]);
    setTotalAvailable(0);
    setSearchTerm('');
    setHasSearched(false);
    setConnectDialogOpen(true);
  };

  const trimmed = searchTerm.trim();
  const canSearch = trimmed.length === 0 || trimmed.length >= 2;

  const handleSearchSubmit = () => {
    if (canSearch) {
      searchObUsers(searchTerm.trim() || undefined);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchSubmit();
    }
  };

  if (loading) {
    return (
      <div className={cn('border rounded-lg p-6 flex items-center justify-center', isProfilePresentation && 'lw-credential-card lw-credential-card--loading')}>
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <div className={cn('border rounded-lg p-6 space-y-4', isProfilePresentation && 'lw-credential-card')} onClick={(e) => e.stopPropagation()}>
        <div className="lw-credential-card__header flex items-start justify-between">
          <div className="w-full space-y-1">
            <div className="flex items-center justify-between gap-3">
              <h3 className="lw-credential-card__title text-lg font-semibold flex items-center gap-2">
                {isProfilePresentation ? 'Optimal Blue' : 'Optimal Blue Integration'}
                {!isProfilePresentation && getStatusBadge()}
              </h3>
              {isProfilePresentation && getStatusBadge()}
            </div>
            <p className="lw-credential-card__note text-sm text-muted-foreground">
              {isProfilePresentation
                ? isAdminView
                  ? 'Admin controls · connection changes live here; plan changes live on Compensation'
                  : 'Connection status only · changes are managed by your admin'
                : 'Connect this profile to Optimal Blue for loan pricing and lock management.'}
            </p>
          </div>
        </div>

        {/* Connected State */}
        {obUserId ? (
          <div className="space-y-4">
            <Card className={cn('border', connected ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200', isProfilePresentation && 'lw-credential-card__status-panel')}>
              <CardContent className={cn('pt-6', isProfilePresentation && 'lw-credential-card__status-content')}>
                <div className={cn('flex items-start justify-between mb-4', isProfilePresentation && 'lw-credential-card__state-heading')}>
                  <div className="flex items-center gap-2">
                    {connected ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-amber-600" />
                    )}
                    <span className={cn('font-medium', connected ? 'text-green-900' : 'text-amber-900')}>
                      {connected ? 'Connected to Optimal Blue' : 'Partial — business channel missing'}
                    </span>
                  </div>
                </div>

                <div className="lw-credential-card__kv-list space-y-3">
                  <div className="lw-credential-card__kv-row flex items-center justify-between">
                    <Label className={connected ? 'text-green-800' : 'text-amber-800'}>OB User ID</Label>
                    <div className="flex items-center gap-2">
                      <code className={cn('lw-credential-card__mono px-2 py-1 bg-white rounded text-sm font-mono', connected ? 'text-green-900' : 'text-amber-900')}>
                        {obUserId}
                      </code>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="Copy Optimal Blue user ID"
                        onClick={copyObId}
                        className={cn('h-8 w-8', connected ? 'hover:bg-green-100' : 'hover:bg-amber-100')}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {obUsername && (
                    <div className="lw-credential-card__kv-row flex items-center justify-between text-sm">
                      <span className={connected ? 'text-green-800' : 'text-amber-800'}>Username</span>
                      <span
                        className={cn('lw-credential-card__mono font-medium', connected ? 'text-green-900' : 'text-amber-900')}
                        title={obUsername}
                      >
                        {obUsername}
                      </span>
                    </div>
                  )}

                  {obLinkedAt && (
                    <div className="lw-credential-card__kv-row flex items-center justify-between text-sm">
                      <span className={connected ? 'text-green-800' : 'text-amber-800'}>Linked at</span>
                      <span className={connected ? 'text-green-900' : 'text-amber-900'}>{obLinkedAt.toLocaleString()}</span>
                    </div>
                  )}

                  <div className="lw-credential-card__kv-row flex items-center justify-between text-sm">
                    <span className={connected ? 'text-green-800' : 'text-amber-800'}>Business Channel</span>
                    {obBusinessChannelId ? (
                      <span
                        className={cn('lw-credential-card__mono font-medium', connected ? 'text-green-900' : 'text-amber-900')}
                        title={obBusinessChannelId}
                      >
                        {obBusinessChannelId}
                      </span>
                    ) : (
                      <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-xs">
                        Missing
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {obState === 'partial' && (
              <Alert className="bg-amber-50 border-amber-200">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-sm text-amber-800">
                  This profile has an OB user but no <strong>business channel</strong>, so Artemis pricing is blocked.
                  {isAdminView ? ' Use Repair from OB to pull it from Optimal Blue.' : ''}
                </AlertDescription>
              </Alert>
            )}

            {isAdminView && (
              <div className="lw-credential-card__actions flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleRefreshIdentity}
                  disabled={syncing}
                  className="gap-2"
                >
                  <RefreshCw className={cn('h-4 w-4', syncing && 'animate-spin')} />
                  Repair from OB
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setUnlinkDialogOpen(true)}
                  disabled={syncing}
                  className={cn(
                    'gap-2 text-red-600 hover:text-red-700 hover:bg-red-50',
                    isProfilePresentation && 'lw-credential-card__action--danger'
                  )}
                >
                  <Unlink className="h-4 w-4" />
                  {isProfilePresentation ? 'Unlink…' : 'Unlink'}
                </Button>
              </div>
            )}
          </div>
        ) : (
          /* Not Connected State */
          <div className="space-y-4">
            <Card className={cn('bg-gray-50 border-gray-200', isProfilePresentation && 'lw-credential-card__empty-panel')}>
              <CardContent className="pt-6">
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="p-3 bg-gray-100 rounded-full">
                    <Users className="h-8 w-8 text-gray-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">Not Connected to Optimal Blue</h4>
                    <p className="text-sm text-gray-600 max-w-sm">
                      Connect this profile to enable loan pricing and lock management with Optimal Blue.
                    </p>
                  </div>
                  {isAdminView && (
                    <Button
                      type="button"
                      onClick={openConnectDialog}
                      disabled={syncing}
                      className="gap-2"
                      size="lg"
                    >
                      <Link2 className="h-5 w-5" />
                      Connect to Optimal Blue
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {isAdminView && !isProfilePresentation && (
              <div className="bg-muted/50 rounded-md p-4 space-y-2">
                <p className="text-xs font-medium text-muted-foreground">What happens when you connect:</p>
                <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                  <li>Searches for existing Optimal Blue user accounts</li>
                  <li>Links the selected OB user to this Lendwise profile</li>
                  <li>Enables per-user pricing and compensation features</li>
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {connectDialogOpen && (
        <LwModal
          className="lw-credential-dialog lw-credential-dialog--connect"
          containerClassName="lw-credential-modal"
          size="wide"
          title={<>Connect to <em>Optimal Blue</em></>}
          sub="Search for the Optimal Blue user to link to this profile."
          onClose={() => setConnectDialogOpen(false)}
          footer={(
            <>
              <LwButton
                variant="sm"
                onClick={() => {
                  setConnectDialogOpen(false);
                  setSearchResults([]);
                  setSelectedObUserId('');
                  setSearchTerm('');
                  setHasSearched(false);
                }}
                disabled={syncing}
              >
                Cancel
              </LwButton>
              <LwButton
                variant="sm-solid"
                onClick={handleLink}
                disabled={syncing || !selectedObUserId}
              >
                {syncing ? (
                  <>
                    <Loader2 className="animate-spin" />
                    Linking...
                  </>
                ) : selectedObUserId ? (
                  <>
                    <Link2 />
                    Link Selected User
                  </>
                ) : (
                  <>
                    <Link2 />
                    Select User
                  </>
                )}
              </LwButton>
            </>
          )}
        >
          <div className="lw-credential-dialog__search">
            <Search aria-hidden="true" />
            <Input
              aria-label="Search Optimal Blue users"
              placeholder="Search by name, username, email, or OB user ID..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
            <LwButton
              variant="sm"
              onClick={handleSearchSubmit}
              disabled={searching || !canSearch}
            >
              {searching && <Loader2 className="animate-spin" />}
              {trimmed ? 'Search' : 'Show Available'}
            </LwButton>
          </div>

          {searching ? (
            <div className="lw-credential-dialog__loading" role="status">
              <Loader2 className="animate-spin" />
              <span>Loading Optimal Blue users...</span>
            </div>
          ) : searchResults.length > 0 ? (
            <>
              <div className="lw-credential-dialog__section-heading">
                <span>Available OB Users</span>
                <span className="lw-credential-dialog__count">
                  {searchResults.length < totalAvailable
                    ? `Showing ${searchResults.length} of ${totalAvailable}`
                    : `${searchResults.length} available`}
                </span>
              </div>
              <RadioGroup
                className="lw-credential-dialog__choices"
                aria-label="Available Optimal Blue users"
                value={selectedObUserId}
                onValueChange={setSelectedObUserId}
              >
                {searchResults.map((user) => {
                  const optionId = `ob-${user.obUserId}`;
                  return (
                    <div
                      key={user.obUserId}
                      className="lw-credential-dialog__choice"
                      data-selected={selectedObUserId === user.obUserId}
                      onClick={() => setSelectedObUserId(user.obUserId)}
                    >
                      <RadioGroupItem value={user.obUserId} id={optionId} />
                      <Label htmlFor={optionId} className="lw-credential-dialog__choice-label">
                        <span className="lw-credential-dialog__choice-copy">
                          <span className="lw-credential-dialog__choice-title">
                            {user.firstName} {user.lastName}
                          </span>
                          <span className="lw-credential-dialog__choice-meta">
                            OB user: {user.username} · Channel {user.businessChannelId}
                            {user.email ? <> · {user.email}</> : null}
                          </span>
                        </span>
                        <LwStatusPill label={user.accessLevel} tone="neutral" />
                      </Label>
                    </div>
                  );
                })}
              </RadioGroup>
            </>
          ) : hasSearched ? (
            <div className="lw-credential-dialog__notice" data-tone="info">
              <Info aria-hidden="true" />
              <p>
                No available Optimal Blue users found. All OB users may already be linked
                to LendWise profiles, or the search term did not match any users.
              </p>
            </div>
          ) : (
            <div className="lw-credential-dialog__empty">
              Click <strong>Show Available</strong> to see all unlinked OB users, or type a
              name to filter.
            </div>
          )}
        </LwModal>
      )}

      {unlinkDialogOpen && (
        <LwModal
          className="lw-credential-dialog lw-credential-dialog--destructive"
          containerClassName="lw-credential-modal"
          size="wide"
          title={<span className="lw-credential-dialog__danger-title">Unlink Optimal Blue User</span>}
          sub="Remove the Optimal Blue connection from this profile? Pricing & plan sync will stop until re-linked."
          onClose={() => setUnlinkDialogOpen(false)}
          footer={(
            <>
              <LwButton
                variant="sm"
                onClick={() => setUnlinkDialogOpen(false)}
                disabled={syncing}
              >
                Cancel
              </LwButton>
              <LwButton
                variant="sm-danger"
                onClick={handleUnlink}
                disabled={syncing}
              >
                {syncing ? (
                  <>
                    <Loader2 className="animate-spin" />
                    Unlinking...
                  </>
                ) : (
                  'Unlink User'
                )}
              </LwButton>
            </>
          )}
        >
          <div className="lw-credential-dialog__notice" data-tone="warning">
            <AlertCircle aria-hidden="true" />
            <p>
              This action will not delete the user from Optimal Blue. It only removes
              the link between LendWise and Optimal Blue.
            </p>
          </div>
          <div className="lw-credential-dialog__summary">
            <span>OB user</span>
            <code>
              {obUsername || obUserId}
              {obBusinessChannelId ? ` · Channel ${obBusinessChannelId}` : ''}
            </code>
          </div>
        </LwModal>
      )}
    </>
  );
};
